import { useEffect, useMemo, useState } from 'react';
import firebase, { db } from '../firebaseConfig';
import { deleteRemoteObject, deleteRemotePrefix } from './uploadService';
import { estimateRenderSec, renderCostUsd } from '../utils/cost';
import { assetRenderJob } from '../utils/eta';
import { composeProofGrade, resolveProofCrop } from '../utils/grade';

// ---- Data model ----------------------------------------------------------
// movies/{id}        : title, synopsis, year, location, durationSec, categories[],
//                      thumbUrl, heroUrl, videoUrl, streamUrl, streamType
//                      ('hls'|'mp4'), hasOriginalAudio (bool),
//                      clips [Clip],                   // ordered video timeline
//                      songs [Song], defaultAudio ('original'|songId),
//                      status ('uploading'|'ready'), featured (bool), createdAt
// songs/{id}         : reusable library track — label, source ('upload'),
//                      url, storageKey, durationSec, sizeBytes, createdAt
//
// Clip (embedded on a movie's clips[]) — an ordered video subclip:
//   { id, label,
//     tripId, assetId,                // provenance (raw footage), optional
//     url,                            // direct/CDN url if served from movies bucket
//     s3Key,                          // raw-bucket key (presigned at play time)
//     inSec, outSec,                  // trim window within the source clip
//     sourceDurationSec }             // full length of the source clip
//
// Song (embedded on a movie's songs[]) — sequenced along the movie timeline:
//   { id, source, label,
//     url|videoId|spotifyUri|scUrl,   // source-specific handle
//     libraryId,                      // link back to songs/{id} (upload)
//     trimStart, trimEnd,             // seconds within the track to play
//     movieOffset,                    // second in the movie when it starts
//     volume }                        // 0..1
//   movies also: hero { posterUrl, delaySec, startSec, durationSec } for the
//   homepage autoplay preview.
// trips/{id}         : name, location, startDate, endDate, description, coverUrl,
//                      lat, lng,                       // for the homepage globe
//                      highlightAssetId, highlightInSec, highlightOutSec,
//                      highlightPosterSec,             // poster still timestamp
//                      highlightPreviewUrl, highlightPosterUrl, highlight{status},
//                      s3Bucket, s3Prefix, storageClass, assetCount, totalBytes,
//                      createdAt
// trips/{id}/assets  : name, sizeBytes, contentType, kind ('video'|'photo'),
//                      s3Key, status ('uploading'|'stored'|'archived'), uploadedAt,
//                      thumbUrl, thumb{status}         // poster for video assets
// --------------------------------------------------------------------------

const ready = () => firebase.apps.length > 0 && db;

export function useCollection(path, { orderBy, dir = 'desc' } = {}) {
  const [docs, setDocs] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!ready()) {
      setDocs([]);
      return undefined;
    }
    let ref = db.collection(path);
    if (orderBy) ref = ref.orderBy(orderBy, dir);
    const unsub = ref.onSnapshot(
      (snap) => setDocs(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => {
        setError(err.message);
        setDocs([]);
      }
    );
    return () => unsub();
  }, [path, orderBy, dir]);

  return { docs, error };
}

export function useDoc(path) {
  const [doc, setDoc] = useState(undefined);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!ready() || !path) return undefined;
    const unsub = db.doc(path).onSnapshot(
      (snap) => setDoc(snap.exists ? { id: snap.id, ...snap.data() } : null),
      (err) => setError(err.message)
    );
    return () => unsub();
  }, [path]);

  return { doc, error };
}

export const useMovies = () => useCollection('movies', { orderBy: 'createdAt' });
export const useTrips = () => useCollection('trips', { orderBy: 'createdAt' });
export const useMovie = (id) => useDoc(id ? `movies/${id}` : null);
export const useTrip = (id) => useDoc(id ? `trips/${id}` : null);

export function useTripAssets(tripId) {
  return useCollection(tripId ? `trips/${tripId}/assets` : 'trips', {
    orderBy: 'uploadedAt',
  });
}

// Live, in-flight uploads (mirrored by the backend from drop-link guests'
// progress heartbeats). Lets the owner watch a contributor's upload fill in
// before the finished file lands as a real asset.
export function useTripUploads(tripId) {
  return useCollection(tripId ? `trips/${tripId}/uploads` : 'trips', {
    orderBy: 'updatedMs',
  });
}

// Dismiss a stale/abandoned in-progress upload row from the owner's view.
export async function deleteTripUpload(tripId, id) {
  if (!ready() || !tripId || !id) return;
  await db.collection('trips').doc(tripId).collection('uploads').doc(id).delete().catch(() => {});
}

// All footage across every trip (collectionGroup), newest first. Used to
// surface uploaded clips on the homepage. Sorted client-side to avoid needing
// a collection-group index.
// `kind` (optional): 'video' | 'photo' — filter BEFORE truncating to `max`, so
// e.g. the clip picker's videos aren't crowded out of the most-recent slice by
// the (far more numerous) frame-grab / proof-strip photo assets.
export function useAllFootage(max = 24, kind = null) {
  const [docs, setDocs] = useState(null);

  useEffect(() => {
    if (!ready()) {
      setDocs([]);
      return undefined;
    }
    const unsub = db.collectionGroup('assets').onSnapshot(
      (snap) => {
        let items = snap.docs.map((d) => ({
          id: d.id,
          tripId: d.ref.parent.parent.id,
          ...d.data(),
        }));
        if (kind === 'video') items = items.filter((a) => a.kind !== 'photo');
        else if (kind === 'photo') items = items.filter((a) => a.kind === 'photo');
        items.sort(
          (a, b) => (b.uploadedAt?.seconds || 0) - (a.uploadedAt?.seconds || 0)
        );
        setDocs(items.slice(0, max));
      },
      () => setDocs([])
    );
    return () => unsub();
  }, [max, kind]);

  return { docs };
}

const now = () => firebase.firestore.FieldValue.serverTimestamp();

export async function createTrip({ name, location, startDate, endDate, description }) {
  const ref = db.collection('trips').doc();
  const s3Prefix = `trips/${ref.id}/`;
  await ref.set({
    name,
    location: location || '',
    startDate: startDate || '',
    endDate: endDate || '',
    description: description || '',
    coverUrl: '',
    lat: null,
    lng: null,
    highlightAssetId: '',
    highlightInSec: 0,
    highlightOutSec: 0,
    highlightPreviewUrl: '',
    highlightPosterUrl: '',
    highlight: { status: 'none' },
    s3Bucket: process.env.REACT_APP_RAW_BUCKET || 'chadha-surf-raw',
    s3Prefix,
    storageClass: 'STANDARD',
    assetCount: 0,
    totalBytes: 0,
    createdAt: now(),
  });
  return ref.id;
}

// Changing any of these re-triggers the backend conditions worker (it rebuilds
// the swell/wind/tide series for the new location/date window).
const CONDITION_TRIGGERS = ['lat', 'lng', 'startDate', 'endDate'];

export async function updateTrip(tripId, patch) {
  const ref = db.collection('trips').doc(tripId);
  let next = patch;
  // If the caller touched location/dates (and isn't already setting conditions),
  // re-queue a fetch — but only when a value actually changed, to respect the
  // tide provider's daily rate limit.
  if (!('conditions' in patch) && CONDITION_TRIGGERS.some((k) => k in patch)) {
    let changed = true;
    try {
      const cur = (await ref.get()).data() || {};
      changed = CONDITION_TRIGGERS.some(
        (k) => k in patch && String(patch[k] ?? '') !== String(cur[k] ?? '')
      );
    } catch {
      changed = true;
    }
    if (changed) next = { ...patch, conditions: { status: 'queued', requestedAt: Date.now() } };
  }
  await ref.set(next, { merge: true });
}

// Manually (re)build a trip's surf-conditions series (the "Refresh conditions"
// button). The backend worker writes the result to tripConditions/{tripId}.
export async function requestTripConditions(tripId) {
  await db
    .collection('trips')
    .doc(tripId)
    .set({ conditions: { status: 'queued', requestedAt: Date.now() } }, { merge: true });
}

// Live hourly swell/wind/tide series for a trip (separate doc to keep the
// often-read trip doc/cards lean). null until the worker has written it.
export const useTripConditions = (tripId) =>
  useDoc(tripId ? `tripConditions/${tripId}` : null);

// Persist a manually-entered conditions series so it survives reload and pins to
// clips/photos like provider data. Stamps source so the UI can label it.
export async function saveManualConditions(tripId, cond) {
  if (!ready() || !tripId) return;
  await db
    .collection('tripConditions')
    .doc(tripId)
    .set({ ...cond, manual: true, savedAt: Date.now() });
}

// Persist the trip's surf-session log: a map of local-date -> array of
// { start, end, note } windows (spot-local "HH:MM"). Kept on the trip doc so it
// rides along with the trip and survives any conditions refetch. Used to mark
// when surf happened on the conditions timeline and to archive sessions for
// long-term swell-pattern study. Pass null/{} to clear.
export async function setTripSurfSessions(tripId, sessions) {
  if (!tripId) return;
  const clean = {};
  Object.entries(sessions || {}).forEach(([date, list]) => {
    const rows = (Array.isArray(list) ? list : [])
      .map((s) => ({
        start: String(s.start || '').slice(0, 5),
        end: String(s.end || '').slice(0, 5),
        note: String(s.note || '').trim().slice(0, 120),
      }))
      .filter((s) => /^\d{1,2}:\d{2}$/.test(s.start) && /^\d{1,2}:\d{2}$/.test(s.end));
    if (rows.length) clean[date] = rows;
  });
  await db
    .collection('trips')
    .doc(tripId)
    .set(
      { surfSessions: Object.keys(clean).length ? clean : firebase.firestore.FieldValue.delete() },
      { merge: true }
    );
}

// Persist the trip's curated highlight clips: an ordered list of
// { assetId, startSec } where startSec is the hover-autoplay start time.
export async function setTripHighlights(tripId, highlights) {
  if (!tripId) return;
  const clean = (Array.isArray(highlights) ? highlights : [])
    .filter((h) => h && h.assetId)
    .map((h) => ({ assetId: String(h.assetId), startSec: Math.max(0, Number(h.startSec) || 0) }))
    // De-dupe by assetId, keeping the first occurrence's order.
    .filter((h, i, arr) => arr.findIndex((x) => x.assetId === h.assetId) === i)
    .slice(0, 24);
  await db
    .collection('trips')
    .doc(tripId)
    .set(
      { highlights: clean.length ? clean : firebase.firestore.FieldValue.delete() },
      { merge: true }
    );
}

// ---- Fan-out jobs (parallel media processing) -----------------------------
// Live view of an ephemeral fan-out job: the job doc plus every per-task
// telemetry doc, for the React-Flow infra visualization. `tasks` is sorted by
// chunk index so the graph lays out deterministically.
export function useJob(jobId) {
  const [job, setJob] = useState(undefined);
  const [tasks, setTasks] = useState([]);

  useEffect(() => {
    if (!ready() || !jobId) {
      setJob(undefined);
      setTasks([]);
      return undefined;
    }
    const unsubJob = db.collection('jobs').doc(jobId).onSnapshot(
      (snap) => setJob(snap.exists ? { id: snap.id, ...snap.data() } : null),
      () => setJob(null)
    );
    const unsubTasks = db
      .collection('jobs')
      .doc(jobId)
      .collection('tasks')
      .onSnapshot(
        (snap) => {
          const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          items.sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
          setTasks(items);
        },
        () => setTasks([])
      );
    return () => {
      unsubJob();
      unsubTasks();
    };
  }, [jobId]);

  return { job, tasks };
}

// All fan-out jobs (live + recent), newest first, for the Live processes page
// and the Costs per-job table.
export function useJobs(max = 100, enabled = true) {
  const [jobs, setJobs] = useState(null);
  useEffect(() => {
    if (!ready() || !enabled) {
      setJobs(null);
      return undefined;
    }
    const unsub = db.collection('jobs').onSnapshot(
      (snap) => {
        const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        items.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
        setJobs(items.slice(0, max));
      },
      () => setJobs([])
    );
    return unsub;
  }, [max, enabled]);
  return jobs;
}

const JOB_ACTIVE = ['planning', 'running', 'decoding', 'stitching'];
export const jobIsActive = (job) => JOB_ACTIVE.includes(job?.status) || !!job?.paused;

// In-flight asset renders (still grab / develop / grade export). Unlike fan-out
// `jobs`, these bake inline on the always-on task and stamp a `renderJob` onto
// the asset. The Processes page shows them alongside the fan-out jobs so every
// running job is visible in one place. Returns the active asset docs (each with
// its `renderJob` + `tripId`), newest first.
export function useActiveRenders(enabled = true) {
  // Three light listeners on the in-flight sub-statuses the workers already
  // write (Firestore can't OR across fields in one query). Merged + de-duped
  // into a single descriptor per asset via assetRenderJob().
  const [parts, setParts] = useState({ stillGrab: [], gradeRender: [], develop: [], clipGradeRender: [] });
  useEffect(() => {
    if (!ready() || !enabled) {
      setParts({ stillGrab: [], gradeRender: [], develop: [], clipGradeRender: [] });
      return undefined;
    }
    const mk = (field, statuses) =>
      db
        .collectionGroup('assets')
        .where(`${field}.status`, 'in', statuses)
        .onSnapshot(
          (snap) =>
            setParts((p) => ({
              ...p,
              [field]: snap.docs.map((d) => ({ id: d.id, tripId: d.ref.parent.parent?.id || null, ...d.data() })),
            })),
          () => setParts((p) => ({ ...p, [field]: [] }))
        );
    const unsubs = [
      mk('stillGrab', ['queued', 'rendering']),
      mk('gradeRender', ['queued', 'rendering']),
      mk('develop', ['queued', 'developing']),
      mk('clipGradeRender', ['queued', 'rendering']),
    ];
    return () => unsubs.forEach((u) => u());
  }, [enabled]);

  return useMemo(() => {
    const byId = new Map();
    [...parts.stillGrab, ...parts.gradeRender, ...parts.develop, ...parts.clipGradeRender].forEach((a) => {
      const key = `${a.tripId}/${a.id}`;
      const rj = assetRenderJob(a);
      if (rj && !byId.has(key)) byId.set(key, { id: a.id, tripId: a.tripId, name: a.name, renderJob: rj });
    });
    return [...byId.values()].sort((x, y) => (y.renderJob.startedAt || 0) - (x.renderJob.startedAt || 0));
  }, [parts]);
}

// Write a control request the orchestrator watches. action: cancel|pause|resume.
export async function controlJob(jobId, action) {
  if (!ready() || !jobId) return;
  await db.collection('jobControls').doc(jobId).set(
    { action, requestedAt: Date.now(), by: firebase.auth().currentUser?.email || 'admin' },
    { merge: true }
  );
}
export const cancelJob = (jobId) => controlJob(jobId, 'cancel');
export const pauseJob = (jobId) => controlJob(jobId, 'pause');
export const resumeJob = (jobId) => controlJob(jobId, 'resume');

// Delete a finished/errored job doc and its per-task telemetry, so the Live
// processes page can be tidied. (The orchestrator never touches done/error
// jobs, so removing them is safe.)
export async function deleteJob(jobId) {
  if (!ready() || !jobId) return;
  const jobRef = db.collection('jobs').doc(jobId);
  const tasks = await jobRef.collection('tasks').get();
  // Tasks subcollections are small (a few chunks + prep/stitch); one batch is
  // plenty, but chunk by 450 just in case.
  let batch = db.batch();
  let n = 0;
  for (const d of tasks.docs) {
    batch.delete(d.ref);
    n += 1;
    if (n % 450 === 0) {
      // eslint-disable-next-line no-await-in-loop
      await batch.commit();
      batch = db.batch();
    }
  }
  await batch.commit();
  await jobRef.delete();
}

// Bulk-delete every errored job. Returns the number removed.
export async function clearErroredJobs() {
  if (!ready()) return 0;
  const snap = await db.collection('jobs').where('status', '==', 'error').get();
  for (const d of snap.docs) {
    // eslint-disable-next-line no-await-in-loop
    await deleteJob(d.id);
  }
  return snap.size;
}

// ---- Guest drop links -----------------------------------------------------
// dropLinks/{token} : tripId, tripName, label, ownerName, passcode (''|code),
//   status ('active'|'revoked'), createdAt, expiresAt (ms epoch),
//   uploadedBytes, uploadedCount, lastUploadAt, lastContributor.
// A videographer with the link uploads through the backend (token-auth), which
// records assets into the trip via the admin SDK — guests never touch Firestore.
function randomToken(bytes = 18) {
  const arr = new Uint8Array(bytes);
  (window.crypto || window.msCrypto).getRandomValues(arr);
  return Array.from(arr)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export async function createDropLink(
  trip,
  { label = '', passcode = '', expiresDays = 14, guestName = '', guestNote = '' } = {}
) {
  const token = randomToken();
  const me = firebase.auth().currentUser;
  const ownerName = me?.displayName || (me?.email ? me.email.split('@')[0] : '') || '';
  await db
    .collection('dropLinks')
    .doc(token)
    .set({
      token,
      tripId: trip.id,
      tripName: trip.name || '',
      label: label || '',
      ownerName,
      guestName: guestName || '',
      guestNote: guestNote || '',
      passcode: passcode || '',
      status: 'active',
      createdAt: now(),
      expiresAt: Date.now() + (Number(expiresDays) || 14) * 86400000,
      uploadedBytes: 0,
      uploadedCount: 0,
    });
  return token;
}

export function useDropLinks(tripId) {
  const [links, setLinks] = useState(null);
  useEffect(() => {
    if (!ready() || !tripId) {
      setLinks([]);
      return undefined;
    }
    const unsub = db
      .collection('dropLinks')
      .where('tripId', '==', tripId)
      .onSnapshot(
        (snap) => {
          const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          docs.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
          setLinks(docs);
        },
        () => setLinks([])
      );
    return unsub;
  }, [tripId]);
  return links;
}

export async function revokeDropLink(token) {
  await db.collection('dropLinks').doc(token).set({ status: 'revoked' }, { merge: true });
}

export async function reactivateDropLink(token, { expiresDays = 14 } = {}) {
  await db
    .collection('dropLinks')
    .doc(token)
    .set({ status: 'active', expiresAt: Date.now() + (Number(expiresDays) || 14) * 86400000 }, { merge: true });
}

export async function deleteDropLink(token) {
  await db.collection('dropLinks').doc(token).delete();
}

// Ask the backend highlight worker to render a trip's hover-preview clip.
export async function requestTripHighlight(tripId) {
  await updateTrip(tripId, { highlight: { status: 'queued', requestedAt: Date.now() } });
}

// Final-output (publish) quality. `height: 0` means "max — match the source's
// native resolution" (re-decoded from RAW for RED clips). `fps: 0` means "match
// the source". Defaults reproduce the original 1080p30 behavior, so existing
// movies are unaffected until the owner opts into something higher.
export const DEFAULT_OUTPUT = { height: 1080, fps: 30, quality: 'standard', redSource: 'proxy', aspect: 'source' };

// Final bake aspect ratio (width : height). "Match source footage" (the default)
// lets the render pipeline mirror the clips' own aspect instead of forcing 16:9.
export const OUTPUT_ASPECT = [
  { value: 'source', label: 'Match source footage' },
  { value: '16:9', label: '16:9 — widescreen' },
  { value: '2.39:1', label: '2.39:1 — anamorphic scope' },
  { value: '1.85:1', label: '1.85:1 — cinema flat' },
  { value: '4:3', label: '4:3 — classic' },
  { value: '1:1', label: '1:1 — square' },
  { value: '9:16', label: '9:16 — vertical' },
  { value: '4:5', label: '4:5 — portrait' },
];

export const OUTPUT_RESOLUTIONS = [
  { value: 1080, label: '1080p (Full HD)' },
  { value: 1440, label: '1440p (QHD)' },
  { value: 2160, label: '2160p (4K UHD)' },
  { value: 0, label: 'Max — original RAW resolution' },
];
export const OUTPUT_FPS = [
  { value: 0, label: 'Match source' },
  { value: 24, label: '24 fps (cinema)' },
  { value: 30, label: '30 fps' },
  { value: 60, label: '60 fps' },
];
export const OUTPUT_QUALITY = [
  { value: 'standard', label: 'Standard' },
  { value: 'high', label: 'High' },
  { value: 'max', label: 'Maximum (near-lossless)' },
];

// Resolve a movie's output settings to a complete object (back-compat: a movie
// with no `output` field renders exactly as before).
export function movieOutput(movie) {
  const o = movie?.output || {};
  return {
    height: o.height == null ? DEFAULT_OUTPUT.height : o.height,
    fps: o.fps == null ? DEFAULT_OUTPUT.fps : o.fps,
    quality: o.quality || DEFAULT_OUTPUT.quality,
    // "Max" resolution can only come from re-decoding RAW, so it forces it.
    redSource: o.height === 0 ? 'raw' : o.redSource || DEFAULT_OUTPUT.redSource,
    // Final bake aspect ratio; 'source' mirrors the footage (no forced 16:9).
    aspect: o.aspect || DEFAULT_OUTPUT.aspect,
    // Optional ProRes 422 HQ archival master alongside the H.264 download.
    prores: !!o.prores,
    // Publish-tail bake settings. null = follow the live config/grading
    // defaults (so config changes keep applying to movies that never chose).
    renditionHeights: Array.isArray(o.renditionHeights) ? o.renditionHeights : null,
    renditionPreset: o.renditionPreset || null,
  };
}

export async function addMovie(movie) {
  const ref = db.collection('movies').doc();
  await ref.set({
    title: movie.title,
    synopsis: movie.synopsis || '',
    year: movie.year || new Date().getFullYear(),
    location: movie.location || '',
    durationSec: movie.durationSec || 0,
    categories: movie.categories || ['Surf'],
    thumbUrl: movie.thumbUrl || '',
    heroUrl: movie.heroUrl || movie.thumbUrl || '',
    videoUrl: movie.videoUrl || '',
    streamUrl: movie.streamUrl || '',
    streamType: movie.streamType || 'hls',
    hasOriginalAudio: movie.hasOriginalAudio !== false,
    clips: movie.clips || [],
    songs: movie.songs || [],
    // New movies start with NO whole-movie grade — each clip is published as-shot
    // unless the owner adds a per-clip or movie-wide look in the Color stage.
    grade: null,
    defaultAudio: movie.defaultAudio || 'original',
    output: movie.output || { ...DEFAULT_OUTPUT },
    status: movie.status || 'ready',
    featured: !!movie.featured,
    hero: movie.hero || { posterUrl: '', delaySec: 4, startSec: 0, durationSec: 12 },
    createdAt: now(),
  });
  return ref.id;
}

// Reference to a movies-bucket object: movies/{movieId}/...
export function buildMovieKey(movieId, parts, fileName) {
  const safe = fileName.replace(/[^\w.\-]+/g, '_');
  return `movies/${movieId}/${parts}${Date.now()}-${safe}`;
}

export async function updateMovie(movieId, patch) {
  await db.collection('movies').doc(movieId).set(patch, { merge: true });
}

export async function deleteMovie(movieId) {
  await db.collection('movies').doc(movieId).delete();
}

// ---- Edit versions -------------------------------------------------------
// Good edits are gold — hours of trimming, ramping and grading. Every publish
// snapshots the exact edit it baked (backend render driver), and the editor
// can save a version manually. Snapshots live in movies/{id}/editVersions as
// the full movie config minus volatile render state; restoring writes back
// ONLY the edit fields (never output URLs or render status).
const EDIT_VERSION_FIELDS = [
  'clips', 'songs', 'grade', 'reframe', 'intro', 'outro',
  'defaultAudio', 'hasOriginalAudio', 'viewerSongs', 'viewerSongDefault',
  'durationSec', 'output', 'exportMaster',
];

export function useEditVersions(movieId) {
  const [versions, setVersions] = useState(null);
  useEffect(() => {
    if (!ready() || !movieId) return undefined;
    const unsub = db
      .collection('movies').doc(movieId).collection('editVersions')
      .orderBy('takenAt', 'desc')
      .limit(50)
      .onSnapshot(
        (snap) => setVersions(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
        () => setVersions([])
      );
    return () => unsub();
  }, [movieId]);
  return versions;
}

// Snapshot the movie's CURRENT edit as a named version (reason: 'manual').
export async function saveEditVersion(movieId, label = '') {
  const snap = await db.collection('movies').doc(movieId).get();
  if (!snap.exists) throw new Error('movie gone');
  const { render: _render, ...editSnapshot } = snap.data();
  await db.collection('movies').doc(movieId).collection('editVersions').add({
    reason: 'manual',
    label: label || `Saved ${new Date().toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}`,
    takenAt: Date.now(),
    clipCount: Array.isArray(editSnapshot.clips) ? editSnapshot.clips.length : 0,
    durationSec: editSnapshot.durationSec || null,
    snapshot: editSnapshot,
  });
}

// Write a version's edit fields back onto the movie doc. Auto-saves the
// current edit first (reason: 'pre-restore') so a restore is never one-way.
export async function restoreEditVersion(movieId, version) {
  const src = version?.snapshot;
  if (!src) throw new Error('version has no snapshot');
  await saveEditVersion(movieId, `Before restoring “${version.label || version.id}”`);
  const patch = {};
  EDIT_VERSION_FIELDS.forEach((f) => {
    if (src[f] !== undefined) patch[f] = src[f];
  });
  await db.collection('movies').doc(movieId).set(patch, { merge: true });
}

export async function deleteEditVersion(movieId, versionId) {
  await db.collection('movies').doc(movieId).collection('editVersions').doc(versionId).delete();
}

// ---- Movie versions (branches) --------------------------------------------
// "Save as new version" forks a movie into a NEW doc — versions are full
// movies (own id, own bakes/URLs; movie ids are baked into S3 paths so a copy
// is the only safe fork). Lineage rides on the child:
//   lineage: { rootId, parentId, branchBaseVersionId, name, branchedAt }
// Both sides also get an editVersions snapshot (reason:'branch') anchoring the
// exact fork point for diffs. Clip asset URLs/s3Keys are shared by reference —
// safe because delete paths never remove published/proxy objects — and the
// clipReframe docs (stabilization tracks, master-time aligned) copy verbatim
// since the timeline is identical at fork time.
const BRANCH_META_FIELDS = [
  'synopsis', 'year', 'location', 'categories', 'thumbUrl', 'heroUrl',
  'videoUrl', 'streamType', 'hero',
  // Derived preview artifacts: the timeline is identical at fork time, so the
  // parent's preview master (clip thumbnails, Reframe/Compare source) and SDK
  // preview proxies are exactly valid. Shared by URL — safe, because
  // deleteMovie only removes the doc, never S3 objects. Staleness detection
  // prompts a rebuild once the branch's cut diverges.
  'master', 'sdkPreviews',
];

// Strip a version suffix — "(branch)", "(branch 2)", "(v3)" — off a title so
// defaults never stack ("… (branch) (branch)").
export const baseMovieTitle = (t) => (t || '').replace(/\s*\((?:branch(?:\s+\d+)?|v\d+)\)\s*$/i, '').trim();

export async function branchMovie(sourceId, { title, name = '' } = {}) {
  const srcSnap = await db.collection('movies').doc(sourceId).get();
  if (!srcSnap.exists) throw new Error('movie gone');
  const src = srcSnap.data();
  let newTitle = (title || '').trim();
  if (!newTitle) {
    // Default: base title + next version number across the whole family
    // (root is v1, so a family of N members makes this fork vN+1).
    const rootId = src.lineage?.rootId || sourceId;
    const fam = await db.collection('movies').where('lineage.rootId', '==', rootId).get();
    newTitle = `${baseMovieTitle(src.title)} (v${fam.size + 2})`;
  }
  const branchedAt = Date.now();
  const { render: _render, ...editSnapshot } = src;

  // 1) Immutable fork base on the SOURCE.
  const baseRef = await db.collection('movies').doc(sourceId).collection('editVersions').add({
    reason: 'branch',
    label: `Branch point → “${newTitle}”`,
    takenAt: branchedAt,
    clipCount: Array.isArray(src.clips) ? src.clips.length : 0,
    durationSec: src.durationSec || null,
    snapshot: editSnapshot,
  });

  // 2) The new version doc: metadata + edit fields, NO render artifacts.
  const ref = db.collection('movies').doc();
  const doc = {
    title: newTitle,
    status: 'ready',
    featured: false,
    createdAt: now(),
    lineage: {
      rootId: src.lineage?.rootId || sourceId,
      parentId: sourceId,
      branchBaseVersionId: baseRef.id,
      name: name.trim(),
      branchedAt,
    },
  };
  BRANCH_META_FIELDS.forEach((f) => { if (src[f] !== undefined) doc[f] = src[f]; });
  EDIT_VERSION_FIELDS.forEach((f) => { if (src[f] !== undefined) doc[f] = src[f]; });
  await ref.set(doc);

  // 3) Copy the per-clip reframe/stabilization analysis docs.
  const reframes = await db.collection('movies').doc(sourceId).collection('clipReframe').get();
  await Promise.all(reframes.docs.map((d) =>
    ref.collection('clipReframe').doc(d.id).set(d.data())
  ));

  // 4) Anchor the child's history at the fork point too.
  await ref.collection('editVersions').add({
    reason: 'branch',
    label: `Branched from “${src.title}”`,
    takenAt: branchedAt,
    parentId: sourceId,
    clipCount: Array.isArray(src.clips) ? src.clips.length : 0,
    durationSec: src.durationSec || null,
    snapshot: editSnapshot,
  });

  return ref.id;
}

// Cherry-pick support: when a clip is pulled from another version, its
// stabilization/reframe analysis doc must come along (tracks are keyed by
// clip id and stored per movie).
export async function copyClipReframeDoc(fromMovieId, toMovieId, clipId) {
  const snap = await db.collection('movies').doc(fromMovieId).collection('clipReframe').doc(clipId).get();
  if (!snap.exists) return false;
  await db.collection('movies').doc(toMovieId).collection('clipReframe').doc(clipId).set(snap.data());
  return true;
}

// One editVersions snapshot by id (the branch fork base lives on the PARENT).
export async function getEditVersion(movieId, versionId) {
  const snap = await db.collection('movies').doc(movieId).collection('editVersions').doc(versionId).get();
  return snap.exists ? { id: snap.id, ...snap.data() } : null;
}

// Live family: the root movie + every version whose lineage points at it.
// Pass any member's doc — the root resolves from its lineage (or itself).
export function useMovieFamily(movie) {
  const rootId = movie ? (movie.lineage?.rootId || movie.id) : null;
  const [root, setRoot] = useState(null);
  const [branches, setBranches] = useState(null);
  useEffect(() => {
    if (!ready() || !rootId) return undefined;
    const unsubRoot = db.collection('movies').doc(rootId).onSnapshot(
      (snap) => setRoot(snap.exists ? { id: snap.id, ...snap.data() } : null),
      () => setRoot(null)
    );
    const unsubKids = db.collection('movies')
      .where('lineage.rootId', '==', rootId)
      .onSnapshot(
        (snap) => setBranches(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
        () => setBranches([])
      );
    return () => { unsubRoot(); unsubKids(); };
  }, [rootId]);
  if (!rootId) return { family: null, rootId: null };
  const family = branches == null ? null : [...(root ? [root] : []), ...branches];
  return { family, rootId };
}

// Flag a movie for server-side render (the backend ffmpeg worker picks it up).
export async function requestRender(movieId) {
  await updateMovie(movieId, { render: { status: 'queued', requestedAt: Date.now() } });
}

// Persist the final-output quality settings for a movie.
export async function updateMovieOutput(movieId, output) {
  await updateMovie(movieId, { output });
}

// Merge a patch into a movie-bake proof sheet (crop requests, notes). The
// sheets themselves are created by tooling; the client only annotates them.
export async function updateProofSheet(sheetId, patch) {
  if (!ready() || !sheetId) return;
  await db.collection('proofSheets').doc(sheetId).set(patch, { merge: true });
}

// Queue a proof-sheet COLOR MODE job (frameProofs worker, kind 'sheet-color'):
// RED clips render {color, neutral} SDK de-log bases × {bare, 2 AI looks} for
// every sampled frame; non-RED clips get the ungraded source + looks. Options:
//   clipIdxs : restrict to some sheet clip idxs (re-render one clip)
//   sdk      : { [idx]: { exposure, toneMap, rolloff } } per-clip SDK dial-in
//   sweep    : { clipIdx, base, exposure } → 3×3 toneMap×rolloff hero grid
//   looks    : { [idx]: [{name, ops}] } reuse looks (skip the AI call)
// Results land on the sheet doc (colorProofs) which the page watches live.
// Queue an SDK preview proxy for one RED movie clip (frameProofs worker, kind
// 'sdk-preview'): the clip's used source window decodes through its chosen RED
// SDK development (clip.redDecode) at edit-proxy res, and the editor plays it
// so the preview shows the TRUE de-log. Result → movie.sdkPreviews[clipId].
export async function queueSdkPreview(movieId, clipId) {
  if (!ready() || !movieId || !clipId) return null;
  const ref = await db.collection('frameProofs').add({
    kind: 'sdk-preview',
    status: 'queued',
    movieId,
    clipId,
    requestedAt: Date.now(),
  });
  return ref.id;
}

export async function queueSheetColorProofs(sheetId, opts = {}) {
  if (!ready() || !sheetId) return null;
  const sheetSnap = await db.collection('proofSheets').doc(sheetId).get();
  const movieId = sheetSnap.exists ? sheetSnap.data().movieId : null;
  const ref = await db.collection('frameProofs').add({
    kind: 'sheet-color',
    status: 'queued',
    sheetId,
    ...(movieId ? { movieId } : {}),
    requestedAt: Date.now(),
    ...opts,
  });
  return ref.id;
}

// Ask the backend to compose a flat (ungraded) MP4 master for color preview.
// `cutSig` (masterCutSig of the clips being baked) rides along so the UI can
// later tell "this master no longer matches the cut" even when durations agree.
export async function requestMaster(movieId, cutSig) {
  await updateMovie(movieId, {
    master: { status: 'queued', requestedAt: Date.now(), ...(cutSig ? { cutSig } : {}) },
  });
}

// ---- Social reframe / export ----------------------------------------------
// Ask the backend reframe worker to (re)analyze a WHOLE movie for subject
// tracking (legacy / one-shot for the full cut).
export async function requestReframe(movieId) {
  await updateMovie(movieId, { reframe: { status: 'queued', scope: 'movie', requestedAt: Date.now() } });
}

// Ask the worker to analyze a SINGLE clip's tile window [t0,t1] (master time).
// Results land on movies/{id}/clipReframe/{clipId}; the movie-level reframe field
// is just the job latch the worker watches.
export async function requestClipReframe(movieId, clipId, { t0 = 0, t1 = 0 } = {}) {
  await updateMovie(movieId, {
    reframe: { status: 'queued', scope: 'clip', clipId, t0, t1, requestedAt: Date.now() },
  });
}

// Persist edited reframe config (subject choice + corrected waypoints).
export async function updateReframe(movieId, patch) {
  await db.collection('movies').doc(movieId).set({ reframe: patch }, { merge: true });
}

// Live map of per-clip subject-tracking analysis: { [clipId]: { status,
// progress, tracks, srcW, srcH, t0, t1, subjectTrackId, message, updatedAt } }.
export function useClipReframes(movieId) {
  const [byClip, setByClip] = useState({});

  useEffect(() => {
    if (!ready() || !movieId) {
      setByClip({});
      return undefined;
    }
    const unsub = db
      .collection('movies').doc(movieId)
      .collection('clipReframe')
      .onSnapshot(
        (snap) => {
          const map = {};
          snap.docs.forEach((d) => { map[d.id] = { id: d.id, ...d.data() }; });
          setByClip(map);
        },
        () => setByClip({})
      );
    return () => unsub();
  }, [movieId]);

  return byClip;
}

// Queue a social export job for a movie. Returns the new export id. `fps` (0 =
// inherit the export master's rate) and `sharpen` (0 = off) are optional.
export async function requestSocialExport(
  movieId,
  { presets, waypoints, subjectTrackId, cropScale, cropSegments, label, fps, sharpen }
) {
  const ref = db.collection('socialExports').doc();
  await ref.set({
    movieId,
    label: label || '',
    presets: presets || [],
    waypoints: waypoints || [],
    subjectTrackId: subjectTrackId || null,
    cropScale: cropScale || 1,
    // Per-clip crop windows (master time). When present the export worker crops
    // each clip's segment with its own window; falls back to waypoints/cropScale.
    cropSegments: Array.isArray(cropSegments) ? cropSegments : null,
    fps: Number(fps) > 0 ? Number(fps) : 0,
    sharpen: Number(sharpen) > 0 ? Number(sharpen) : 0,
    status: 'queued',
    progress: 0,
    outputs: [],
    requestedAt: now(),
  });
  return ref.id;
}

export async function deleteExport(exportId) {
  await db.collection('socialExports').doc(exportId).delete();
}

// Live list of a movie's social export jobs, newest first.
export function useExports(movieId) {
  const [docs, setDocs] = useState(null);

  useEffect(() => {
    if (!ready() || !movieId) {
      setDocs([]);
      return undefined;
    }
    const unsub = db
      .collection('socialExports')
      .where('movieId', '==', movieId)
      .onSnapshot(
        (snap) => {
          const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          items.sort((a, b) => (b.requestedAt?.seconds || 0) - (a.requestedAt?.seconds || 0));
          setDocs(items);
        },
        () => setDocs([])
      );
    return () => unsub();
  }, [movieId]);

  return { docs };
}

// ---- Global song library --------------------------------------------------
export const useSongLibrary = () => useCollection('songs', { orderBy: 'createdAt' });

// movies/library/songs/<ts>-name.mp3 (stays within the movies-bucket prefix).
export function buildLibrarySongKey(fileName) {
  const safe = fileName.replace(/[^\w.\-]+/g, '_');
  return `movies/library/songs/${Date.now()}-${safe}`;
}

export async function addLibrarySong(song) {
  const ref = db.collection('songs').doc();
  await ref.set({
    label: song.label || 'Untitled',
    source: song.source || 'upload',
    url: song.url || '',
    storageKey: song.storageKey || '',
    videoId: song.videoId || '',
    thumb: song.thumb || '',
    durationSec: song.durationSec || 0,
    sizeBytes: song.sizeBytes || 0,
    peaks: song.peaks || [],
    waveform: song.waveform || { status: song.autoAnalyze ? 'queued' : 'none' },
    createdAt: now(),
  });
  return ref.id;
}

export async function deleteLibrarySong(songId) {
  await db.collection('songs').doc(songId).delete();
}

// Ask the backend waveform worker to (re)analyze a library song's peaks.
export async function requestWaveform(songId) {
  await db
    .collection('songs')
    .doc(songId)
    .set({ waveform: { status: 'queued', requestedAt: Date.now() } }, { merge: true });
}

// RAW photo formats we ingest + develop with LibRaw. Extension-based since RAW
// files often arrive with an empty/octet-stream MIME type.
export const RAW_FORMATS = [
  'dng', 'cr2', 'cr3', 'nef', 'nrw', 'arw', 'sr2', 'srf', 'raf', 'orf', 'rw2',
  'pef', 'srw', 'raw', '3fr', 'fff', 'iiq', 'kdc', 'dcr', 'mos', 'mef', 'erf', 'gpr',
];
export function rawFormatOf(name = '') {
  const ext = String(name).split('.').pop().toLowerCase();
  return RAW_FORMATS.includes(ext) ? ext : '';
}
export const isRawName = (name) => !!rawFormatOf(name);

// RAW *video* formats (REDCODE). These can't be read by ffmpeg/browsers; the
// backend R3D proxy worker decodes them into a streamable HLS proxy. R3D clips
// arrive inside a `.RDC` folder, often spanned across several .R3D part files.
export const RAW_VIDEO_FORMATS = ['r3d'];
export function rawVideoFormatOf(name = '') {
  const ext = String(name).split('.').pop().toLowerCase();
  return RAW_VIDEO_FORMATS.includes(ext) ? ext : '';
}
export const isRawVideoName = (name) => !!rawVideoFormatOf(name);

// Non-RED video masters at or above this size auto-get a 1080p streaming proxy.
export const VIDEO_PROXY_MIN_BYTES = 2 * 1024 ** 3; // 2 GiB

// Queue (or re-queue) a fast-loading 1080p HLS proxy for a non-RED video asset.
export async function requestVideoProxy(tripId, assetId) {
  await assetRef(tripId, assetId).set(
    { vproxy: { status: 'queued', requestedAt: Date.now() } },
    { merge: true }
  );
}

export async function recordAsset(tripId, asset) {
  const ref = db.collection('trips').doc(tripId).collection('assets').doc();
  const rawFormat = rawFormatOf(asset.name);
  const isRaw = !!rawFormat;
  const isRawVideo = !!asset.isRawVideo || isRawVideoName(asset.name);
  const kind = asset.kind || (isRaw ? 'photo' : 'video');
  // ffprobe can read regular video/photo (not RAW stills or R3D); queue those.
  const needsMeta = (kind === 'video' && !isRawVideo) || (kind === 'photo' && !isRaw);
  const doc = {
    name: asset.name,
    sizeBytes: asset.sizeBytes,
    contentType: asset.contentType || '',
    kind,
    isRaw,
    rawFormat,
    s3Key: asset.s3Key,
    status: asset.status || 'stored',
    thumbUrl: '',
    grade: null,
    // RAW photos develop on the backend (16-bit proxy); videos get a poster.
    develop: { status: isRaw ? 'queued' : 'none' },
    gradeRender: { status: 'none' },
    // RAW video (R3D) can't be read by ffmpeg, so its poster comes from the
    // proxy worker (decoded frame); regular video posters come from `thumbs`.
    thumb: { status: kind === 'video' && !isRawVideo ? 'queued' : 'none' },
    // Preserve the original technical metadata (fps/codec/camera/…) for archival.
    meta: { status: needsMeta ? 'queued' : 'none' },
    // Provenance for media-asset lineage tagging.
    lineage: {
      origin: asset.origin || 'upload',
      via: asset.uploadedVia || asset.via || 'uploader',
      originalName: asset.name,
      contributor: asset.contributor || '',
      addedAt: Date.now(),
      // Uploader identity (server-verified IP/geo + browser fingerprint/GPS).
      ...(asset.uploaderMeta ? { uploader: asset.uploaderMeta } : {}),
    },
    tags: Array.isArray(asset.tags) ? asset.tags : [],
    uploadedAt: now(),
  };
  if (isRawVideo) {
    doc.isRawVideo = true;
    doc.rawVideoFormat = asset.rdcFormat || rawVideoFormatOf(asset.name) || 'r3d';
    // Decode -> streamable HLS proxy (UHD/H.265) on the movies CDN.
    doc.proxy = { status: 'queued' };
    if (asset.rdc) doc.rdc = true;
    if (asset.folderPrefix) doc.folderPrefix = asset.folderPrefix;
    if (Array.isArray(asset.parts)) doc.parts = asset.parts;
  } else if (kind === 'video' && (asset.sizeBytes || 0) >= VIDEO_PROXY_MIN_BYTES) {
    // Large non-RED masters load + scrub slowly off the original; auto-build a
    // 1080p HLS proxy for fast playback (the original stays archival).
    doc.vproxy = { status: 'queued' };
  }
  await ref.set(doc);
  await db
    .collection('trips')
    .doc(tripId)
    .set(
      {
        assetCount: firebase.firestore.FieldValue.increment(1),
        totalBytes: firebase.firestore.FieldValue.increment(asset.sizeBytes || 0),
      },
      { merge: true }
    );
  return ref.id;
}

// Queue a frame grab → trip photo. The browser scrubs a (proxy) preview and
// picks a time + optional crop; the backend `stills` worker decodes the chosen
// frame from the ORIGINAL max-res source (incl. RED .R3D), bakes the clip's
// saved color/light grade and the crop, and fills in s3Key/thumbUrl. A
// placeholder photo shows in the trip immediately and resolves when rendered.
//   source   the source clip asset (needs id, name, grade, isRawVideo)
//   timeSec  playhead time of the grabbed frame
//   format   'jpg' | 'png'
//   crop     optional { x, y, w, h } normalized to the source frame (0..1)
//   applyGrade  whether to bake the clip's saved look (default true)
//   grade    optional grade to bake instead of the source's saved one (e.g. a
//            live, unsaved look from the color editor). Omit to use source.grade.
//   name     optional custom display name for the still (extension auto-added)
//   tags     optional array (or comma string) of labels for the new photo
export async function createStillGrabAsset(
  tripId,
  { source, timeSec, frameIndex = null, format = 'jpg', crop = null, applyGrade = true, grade, startingLook = null, redSource = false, name: customName, tags, movieId = '', movieTitle = '', autoGrade = false, clipLayer = null, override = null, proofOf = null, revisedFromAssetId = '', nlPrompt = '', upscale = null, proofSheet = null, proofFrame = null }
) {
  const ref = db.collection('trips').doc(tripId).collection('assets').doc();
  // Every grab is saved as a non-destructive PNG "negative": the look AND the
  // crop are kept on the asset (never baked into the master), so you can re-grade,
  // re-expose, re-crop and reframe later with full latitude — and export any time.
  // RED keeps full 16-bit latitude; other sources are 8-bit but the lossless PNG
  // negative still re-grades cleanly. (The `format` arg is kept for compatibility.)
  const nonDestructive = true;
  const ext = 'png';
  const stamp = Math.max(0, Math.round(Number(timeSec) || 0));
  const mm = String(Math.floor(stamp / 60)).padStart(2, '0');
  const ss = String(stamp % 60).padStart(2, '0');
  const baseName = String(source?.name || 'clip').replace(/\.[^.]+$/, '');
  const autoName = `${baseName} — frame ${mm}-${ss}.${ext}`;
  const typed = String(customName || '').trim();
  const name = typed ? (/\.(jpe?g|png)$/i.test(typed) ? typed : `${typed}.${ext}`) : autoName;
  const look = applyGrade ? (grade !== undefined ? grade : source?.grade || null) : null;
  const cleanTags = Array.from(
    new Set(
      (Array.isArray(tags) ? tags : String(tags || '').split(','))
        .map((t) => String(t || '').trim())
        .filter(Boolean)
    )
  ).slice(0, 30);
  if (proofOf && !cleanTags.includes('clip-proof')) cleanTags.push('clip-proof');
  const doc = {
    name,
    sizeBytes: 0,
    contentType: ext === 'png' ? 'image/png' : 'image/jpeg',
    kind: 'photo',
    isRaw: false,
    rawFormat: '',
    s3Key: '',
    status: 'rendering',
    thumbUrl: '',
    displayUrl: '',
    derivatives: {},
    // Non-destructive masters keep the look + crop on the asset (applied to
    // display derivatives + exports, never baked into the full-frame negative)
    // so both stay fully editable in the photo editor.
    grade: nonDestructive ? look || null : null,
    crop: crop && Number(crop.w) > 0 && Number(crop.h) > 0 ? crop : null,
    // The look applied automatically at grab time (e.g. RED IPP2 Natural), shown
    // in the editor so it's clear the "creative" grade has a known starting point
    // that can be edited or removed back to the neutral base.
    startingLook: applyGrade && startingLook && startingLook.name ? startingLook : null,
    develop: { status: 'none' },
    gradeRender: { status: 'none' },
    thumb: { status: 'none' },
    meta: { status: 'none' },
    stillGrab: {
      status: 'queued',
      sourceAssetId: source?.id || '',
      timeSec: Number(timeSec) || 0,
      // Exact source-frame index (overrides timeSec→frame rounding when set).
      // Useful for RED where the native frame rate differs from the proxy clock.
      ...(Number.isFinite(Number(frameIndex)) && Number(frameIndex) >= 0
        ? { frameIndex: Math.round(Number(frameIndex)) }
        : {}),
      format: ext,
      applyGrade: !!applyGrade,
      nonDestructive,
      crop: crop && crop.w > 0 && crop.h > 0 ? crop : null,
      grade: look || null,
      // Clip grading sessions: when autoGrade is on the worker computes a frozen
      // per-frame base, then bakes composeProofGrade(base, clipLayer, override).
      ...(autoGrade ? { autoGrade: true } : {}),
      ...(clipLayer ? { clipLayer } : {}),
      ...(override ? { override } : {}),
      // Reframe → AI upscale BEFORE grading. The worker derives a display-referred
      // working negative (crop → tone-map → Replicate upscale) and grades that, so
      // the look + masks resolve on real pixels. Master negative stays full-frame.
      ...(upscale && upscale.enabled ? { upscale: { enabled: true, model: upscale.model || 'swinir', scale: Number(upscale.scale) || 2 } } : {}),
      requestedAt: Date.now(),
    },
    // Live render-job descriptor read by the trip thumbnail + Processes page so a
    // baking still shows an ETA + estimated cost until the worker finishes. The
    // worker refines this (rendering → done/error) with the metered actual cost.
    renderJob: {
      kind: 'still',
      status: 'queued',
      title: name,
      sourceName: source?.name || '',
      tripId,
      etaSec: estimateRenderSec('still', !!redSource),
      costEstUsd: Math.round(renderCostUsd(estimateRenderSec('still', !!redSource)) * 1e4) / 1e4,
      requestedAt: Date.now(),
    },
    lineage: {
      origin: 'frame-grab',
      via: redSource ? 'RED frame grab' : 'frame grab',
      originalName: name,
      contributor: '',
      sourceAssetId: source?.id || '',
      sourceName: source?.name || '',
      sourceTimeSec: Number(timeSec) || 0,
      sourceIsRaw: !!redSource,
      // When grabbed from inside a movie editor, remember which movie so the
      // Review tab can gather every still taken from this edit.
      movieId: movieId || '',
      movieTitle: movieTitle || '',
      // Natural-language revision provenance: which prior variation this version
      // was spun from, and the note that produced it. Powers the edit tree and a
      // future "learn from accepted edits" pass that feeds the auto-grade priors.
      ...(revisedFromAssetId ? { revisedFromAssetId } : {}),
      ...(nlPrompt ? { nlPrompt: String(nlPrompt).slice(0, 280) } : {}),
      addedAt: Date.now(),
    },
    // Clip grading sessions: proofs are normal frame-grabs that point back at the
    // clip they belong to so a session can gather + re-bake them as a set.
    ...(proofOf ? { proofOf, autoGradeBase: null, override: override || null, gradeSummary: null } : {}),
    // Proof-sheet grouping (contact-sheet review page): the sheet this still
    // belongs to and its frame number, so a strip lays out in clip order.
    ...(proofSheet && proofSheet.name ? { proofSheet } : {}),
    ...(Number.isFinite(Number(proofFrame)) ? { proofFrame: Math.round(Number(proofFrame)) } : {}),
    tags: cleanTags,
    uploadedAt: now(),
  };
  await ref.set(doc);
  await db
    .collection('trips')
    .doc(tripId)
    .set({ assetCount: firebase.firestore.FieldValue.increment(1) }, { merge: true });
  return ref.id;
}

// Re-queue the R3D -> HLS proxy build for a raw-video asset.
export async function requestProxy(tripId, assetId) {
  await assetRef(tripId, assetId).set(
    { proxy: { status: 'queued', requestedAt: Date.now() } },
    { merge: true }
  );
}

// ---- RAW develop / photo grade --------------------------------------------
export const useAsset = (tripId, assetId) =>
  useDoc(tripId && assetId ? `trips/${tripId}/assets/${assetId}` : null);

// Every frame-grab still that belongs to a movie: subscribes to each trip the
// movie's clips come from and keeps the photos whose lineage points at this
// movie (movieId stamped at grab time) or at one of the movie's source clips
// (back-compat for grabs taken before movieId existed). Newest first.
export function useMovieStills(movieId, clips = []) {
  const tripKey = useMemo(
    () => Array.from(new Set((clips || []).map((c) => c.tripId).filter(Boolean))).sort().join(','),
    [clips]
  );
  const clipAssetIds = useMemo(
    () => new Set((clips || []).map((c) => c.assetId).filter(Boolean)),
    [clips]
  );
  const [byTrip, setByTrip] = useState({});

  useEffect(() => {
    const tripIds = tripKey ? tripKey.split(',') : [];
    if (!ready() || !tripIds.length) {
      setByTrip({});
      return undefined;
    }
    const unsubs = tripIds.map((tid) =>
      db
        .collection('trips')
        .doc(tid)
        .collection('assets')
        .where('kind', '==', 'photo')
        .onSnapshot(
          (snap) =>
            setByTrip((prev) => ({
              ...prev,
              [tid]: snap.docs.map((d) => ({ id: d.id, tripId: tid, ...d.data() })),
            })),
          () => setByTrip((prev) => ({ ...prev, [tid]: [] }))
        )
    );
    return () => unsubs.forEach((u) => u());
  }, [tripKey]);

  return useMemo(() => {
    const all = Object.values(byTrip).flat();
    return all
      .filter(
        (a) =>
          a?.lineage?.origin === 'frame-grab' &&
          ((movieId && a.lineage.movieId === movieId) ||
            clipAssetIds.has(a.lineage.sourceAssetId))
      )
      .sort((a, b) => (b.uploadedAt?.seconds || 0) - (a.uploadedAt?.seconds || 0));
  }, [byTrip, movieId, clipAssetIds]);
}

function assetRef(tripId, assetId) {
  return db.collection('trips').doc(tripId).collection('assets').doc(assetId);
}

// One-off read of an asset doc (e.g. to backfill a clip's duration/proxy URL
// that wasn't known when it was first added to a movie). Returns null if gone.
export async function getAssetDoc(tripId, assetId) {
  if (!ready() || !tripId || !assetId) return null;
  const snap = await assetRef(tripId, assetId).get();
  return snap.exists ? { id: snap.id, ...snap.data() } : null;
}

// Live subscription to a single asset doc (e.g. to watch clip-audio waveform
// analysis land). Returns an unsubscribe fn; cb gets { id, ...data } or null.
export function subscribeAsset(tripId, assetId, cb) {
  if (!ready() || !tripId || !assetId) { cb(null); return () => {}; }
  return assetRef(tripId, assetId).onSnapshot(
    (snap) => cb(snap.exists ? { id: snap.id, ...snap.data() } : null),
    () => cb(null)
  );
}

// Ask the waveform worker to extract a clip's own audio peaks (the sound
// recorded with the footage). Stored on the asset as audioPeaks / hasAudio and
// shared by every movie that uses the clip. No-op if already analyzed.
export async function requestAssetWaveform(tripId, assetId) {
  if (!ready() || !tripId || !assetId) return;
  await assetRef(tripId, assetId).set(
    { audioWaveform: { status: 'queued', requestedAt: Date.now() } },
    { merge: true }
  );
}

// Rename an asset's display name. This only touches the `name` field — the S3
// key never changes, so any movie clips that reference this footage by key
// keep playing untouched.
export async function renameAsset(tripId, assetId, name) {
  const clean = String(name || '').trim();
  if (!clean) return;
  await assetRef(tripId, assetId).set({ name: clean }, { merge: true });
}

// Lineage tags: free-form labels for organizing/provenance (e.g. "drone",
// "slow-mo", "videographer:Alex"). Stored as a clean, de-duplicated array.
export async function updateAssetTags(tripId, assetId, tags) {
  const clean = Array.from(
    new Set((tags || []).map((t) => String(t || '').trim()).filter(Boolean))
  ).slice(0, 30);
  await assetRef(tripId, assetId).set({ tags: clean }, { merge: true });
}

// The photographer credited for an asset. This is the owner's authoritative
// label and overrides the name captured at upload time (lineage.contributor) —
// useful for crediting footage the owner uploaded themselves, or fixing/adding
// a credit after the fact. Pass an empty string to clear the override and fall
// back to the upload-time name.
export async function setAssetPhotographer(tripId, assetId, name) {
  const clean = String(name || '').trim().slice(0, 120);
  await assetRef(tripId, assetId).set(
    { photographer: clean || firebase.firestore.FieldValue.delete() },
    { merge: true }
  );
}

// The surfer riding in an asset. Purely owner-set (there is no upload-time
// equivalent to fall back to). Pass an empty string to clear the tag.
export async function setAssetSurfer(tripId, assetId, name) {
  const clean = String(name || '').trim().slice(0, 120);
  await assetRef(tripId, assetId).set(
    { surfer: clean || firebase.firestore.FieldValue.delete() },
    { merge: true }
  );
}

// Re-run the metadata probe for an asset (e.g. after a failed first pass).
export async function requestAssetMeta(tripId, assetId) {
  await assetRef(tripId, assetId).set(
    { meta: { status: 'queued', requestedAt: Date.now() } },
    { merge: true }
  );
}

// Manual capture time (epoch ms) for assets whose metadata lacks a recording
// date. Used to line a clip/photo up with the trip's surf-conditions series.
// Pass null to clear the override and fall back to the probed `recordedAt`.
export async function setAssetCapturedAt(tripId, assetId, epochMs) {
  if (epochMs == null) {
    await assetRef(tripId, assetId).set(
      { capturedAtOverride: firebase.firestore.FieldValue.delete() },
      { merge: true }
    );
    return;
  }
  const ms = Number(epochMs);
  if (!Number.isFinite(ms)) return;
  await assetRef(tripId, assetId).set({ capturedAtOverride: ms }, { merge: true });
}

// The current best-known capture instant (epoch ms) for an asset: a manual
// override wins, else the probed recording date. Null if neither is known.
function assetBaseCaptureMs(a) {
  if (Number.isFinite(a?.capturedAtOverride)) return a.capturedAtOverride;
  const rec = a?.recordedAt || a?.metadata?.recordedAt;
  const ms = rec ? Date.parse(rec) : NaN;
  return Number.isFinite(ms) ? ms : null;
}

// Bulk-correct a wrong camera clock: shift each asset's capture time by `hours`
// (may be fractional / negative) and store it as a capturedAtOverride so the
// footage lines up with the trip's surf-conditions series. Assets with no known
// time are skipped. Returns the number of assets actually shifted.
export async function shiftAssetsCapturedTime(tripId, assets, hours) {
  const deltaMs = Math.round(Number(hours) * 3600 * 1000);
  if (!Number.isFinite(deltaMs) || deltaMs === 0 || !Array.isArray(assets) || !assets.length) {
    return 0;
  }
  let batch = db.batch();
  let n = 0;
  let ops = 0;
  for (const a of assets) {
    const base = assetBaseCaptureMs(a);
    if (base == null) continue;
    batch.set(assetRef(tripId, a.id), { capturedAtOverride: base + deltaMs }, { merge: true });
    n += 1;
    ops += 1;
    if (ops >= 400) {
      await batch.commit(); // eslint-disable-line no-await-in-loop
      batch = db.batch();
      ops = 0;
    }
  }
  if (ops > 0) await batch.commit();
  return n;
}

// Clear manual capture-time overrides on a set of assets (revert to the probed
// recording date). Returns the count cleared.
export async function clearAssetsCapturedTime(tripId, assets) {
  if (!Array.isArray(assets) || !assets.length) return 0;
  let batch = db.batch();
  let n = 0;
  let ops = 0;
  for (const a of assets) {
    if (!Number.isFinite(a?.capturedAtOverride)) continue;
    batch.set(
      assetRef(tripId, a.id),
      { capturedAtOverride: firebase.firestore.FieldValue.delete() },
      { merge: true }
    );
    n += 1;
    ops += 1;
    if (ops >= 400) {
      await batch.commit(); // eslint-disable-line no-await-in-loop
      batch = db.batch();
      ops = 0;
    }
  }
  if (ops > 0) await batch.commit();
  return n;
}

// Rename a library song (label only; storage key/URL are untouched).
export async function renameLibrarySong(songId, label) {
  const clean = String(label || '').trim();
  if (!clean) return;
  await db.collection('songs').doc(songId).set({ label: clean }, { merge: true });
}

// ---- Soft delete / trash --------------------------------------------------
// Deleting media is recoverable for TRASH_RETENTION_DAYS: a Firestore flag
// hides it from normal views while the S3 object stays put. A backend cron
// permanently purges the object + doc once the window elapses. "Vaporize"
// skips the wait and deletes immediately.
export const TRASH_RETENTION_DAYS = 90;
const RETENTION_MS = TRASH_RETENTION_DAYS * 24 * 60 * 60 * 1000;
const DELETE_FIELD = firebase.firestore.FieldValue.delete();

export const isTrashed = (x) => x?.deleted?.status === 'trashed';
export const purgeDaysLeft = (x) => {
  const after = x?.deleted?.purgeAfter || 0;
  return Math.max(0, Math.ceil((after - Date.now()) / 86_400_000));
};
const trashStamp = () => {
  const at = Date.now();
  return { deleted: { status: 'trashed', at, purgeAfter: at + RETENTION_MS } };
};

// Asset trash / restore / vaporize.
export async function trashAsset(tripId, assetId) {
  await assetRef(tripId, assetId).set(trashStamp(), { merge: true });
}
export async function restoreAsset(tripId, assetId) {
  await assetRef(tripId, assetId).set({ deleted: DELETE_FIELD }, { merge: true });
}
export async function vaporizeAsset(tripId, asset) {
  // A .RDC asset is a folder of part files; remove every one. Otherwise it's a
  // single object.
  const keys = Array.isArray(asset?.parts) && asset.parts.length
    ? asset.parts.map((p) => p.s3Key).filter(Boolean)
    : asset?.s3Key
    ? [asset.s3Key]
    : [];
  for (const key of keys) {
    try {
      // eslint-disable-next-line no-await-in-loop
      await deleteRemoteObject({ key, bucket: 'raw' });
    } catch (err) {
      // Surface so the UI can warn; the doc is left intact for a retry.
      throw new Error(`Could not delete the file from storage: ${err.message || err}`);
    }
  }
  await assetRef(tripId, asset.id).delete();
  await db
    .collection('trips')
    .doc(tripId)
    .set(
      {
        assetCount: firebase.firestore.FieldValue.increment(-1),
        totalBytes: firebase.firestore.FieldValue.increment(-(asset.sizeBytes || 0)),
      },
      { merge: true }
    );
}

// Library song trash / restore / vaporize.
export async function trashSong(songId) {
  await db.collection('songs').doc(songId).set(trashStamp(), { merge: true });
}
export async function restoreSong(songId) {
  await db.collection('songs').doc(songId).set({ deleted: DELETE_FIELD }, { merge: true });
}
export async function vaporizeSong(song) {
  if (song?.storageKey) {
    try {
      await deleteRemoteObject({ key: song.storageKey, bucket: 'movies' });
    } catch (err) {
      throw new Error(`Could not delete the song from storage: ${err.message || err}`);
    }
  }
  await db.collection('songs').doc(song.id).delete();
}

// Trip (folder) trash / restore: cascades to every asset in the trip. Batched
// in chunks to stay under Firestore's 500-write limit.
async function cascade(tripId, value) {
  const snap = await db.collection('trips').doc(tripId).collection('assets').get();
  const refs = snap.docs.map((d) => d.ref);
  for (let i = 0; i < refs.length; i += 400) {
    const batch = db.batch();
    refs.slice(i, i + 400).forEach((r) => batch.set(r, value, { merge: true }));
    // eslint-disable-next-line no-await-in-loop
    await batch.commit();
  }
}
export async function trashTrip(tripId) {
  await cascade(tripId, trashStamp());
  await db.collection('trips').doc(tripId).set(trashStamp(), { merge: true });
}
export async function restoreTrip(tripId) {
  await cascade(tripId, { deleted: DELETE_FIELD });
  await db.collection('trips').doc(tripId).set({ deleted: DELETE_FIELD }, { merge: true });
}
export async function vaporizeTrip(trip) {
  const prefix = trip?.s3Prefix || `trips/${trip.id}/`;
  try {
    await deleteRemotePrefix({ prefix, bucket: 'raw' });
  } catch (err) {
    throw new Error(`Could not delete the trip's files from storage: ${err.message || err}`);
  }
  const snap = await db.collection('trips').doc(trip.id).collection('assets').get();
  const refs = snap.docs.map((d) => d.ref);
  for (let i = 0; i < refs.length; i += 400) {
    const batch = db.batch();
    refs.slice(i, i + 400).forEach((r) => batch.delete(r));
    // eslint-disable-next-line no-await-in-loop
    await batch.commit();
  }
  await db.collection('trips').doc(trip.id).delete();
}

// (Re)develop a RAW into a 16-bit proxy + thumbnail on the backend.
export async function requestDevelop(tripId, assetId) {
  await assetRef(tripId, assetId).set(
    { develop: { status: 'queued', requestedAt: Date.now() } },
    { merge: true }
  );
}

// Save a non-destructive grade onto a photo asset.
export async function updateAssetGrade(tripId, assetId, grade) {
  await assetRef(tripId, assetId).set({ grade }, { merge: true });
}

// Snapshot the model's auto-grade (the baked "AI" result) onto the asset once, so
// the editor can always view/restore it as a reference even after the working
// grade is reworked from the negative. Written a single time on first open.
export async function updateAssetAiGrade(tripId, assetId, aiGrade) {
  await assetRef(tripId, assetId).set({ aiGrade }, { merge: true });
}

// Persist the reference-look frame pairing for a clip asset (the "match the
// photog's graded delivery" workflow). Only ids/urls/times are stored —
// `{ refAssetId, refName, refUrl, refS3Key, pairs: [{ t, refT }] }` with times
// in source seconds; the frames themselves live in S3 and are recaptured on
// demand. Pass null to clear.
export async function updateAssetGradeRef(tripId, assetId, gradeRef) {
  await assetRef(tripId, assetId).set(
    { gradeRef: gradeRef || firebase.firestore.FieldValue.delete() },
    { merge: true }
  );
}

// Persist a keyframed (time-varying) clip grade. `keyframes` = [{ id, t, grade }]
// in clip-source seconds; `segment` = { inSec, outSec } (the region of interest).
// We also mirror the first keyframe into `grade` so single-grade consumers
// (movie render, frame grabs) still get a sensible static look from this clip.
export async function updateClipKeyframes(tripId, assetId, { keyframes, segment }) {
  const kfs = Array.isArray(keyframes) ? keyframes : [];
  const patch = {
    gradeKeyframes: kfs,
    gradeSegment:
      segment && Number.isFinite(Number(segment.inSec)) && Number.isFinite(Number(segment.outSec))
        ? { inSec: Number(segment.inSec) || 0, outSec: Number(segment.outSec) || 0 }
        : null,
  };
  if (kfs.length) {
    const first = [...kfs].sort((a, b) => (a.t || 0) - (b.t || 0))[0];
    if (first?.grade) patch.grade = first.grade;
  }
  await assetRef(tripId, assetId).set(patch, { merge: true });
}

// Queue a server-side bake of the keyframed grade into a graded MP4. The
// clipGrade worker reads gradeKeyframes / gradeSegment off the asset and writes
// progress to renderJob + the result to `clipGrade`.
// Queue the server-side clip bake. `output` controls final resolution:
//   { source: 'master'|'proxy', heightTarget: 0|2160|1080, quality: 'standard'|'high'|'max' }
//   master → re-decode the RED RAW at native res (up to 8K); proxy → fast ≤4K.
export async function requestClipGradeRender(tripId, assetId, output = null) {
  const spec = output && typeof output === 'object'
    ? {
        source: output.source === 'proxy' ? 'proxy' : 'master',
        heightTarget: Number(output.heightTarget) > 0 ? Math.round(Number(output.heightTarget)) : 0,
        quality: ['standard', 'high', 'max'].includes(output.quality) ? output.quality : 'high',
      }
    : { source: 'master', heightTarget: 0, quality: 'high' };
  await assetRef(tripId, assetId).set(
    // `override: null` clears any leftover movie-preview override (set+merge
    // would otherwise keep it and misroute this render to moviePreview).
    { clipGradeRender: { status: 'queued', output: spec, override: null, requestedAt: Date.now() } },
    { merge: true }
  );
}

// Bake a HIGH-RES graded preview of a MOVIE clip's trimmed segment (the movie
// editor's "Bake HQ preview"). Reuses the clip-grade worker but overrides the
// asset's own look with the movie-local one: `override` carries the clip grade
// (+ optional keyframes, t in source-seconds from t0), the movie-wide grade
// stacked on top, and the [t0,t1] source trim window. The result lands on
// asset.moviePreview so it never clobbers the standalone Clip Color Studio's
// clipGrade delivery.
export async function requestMovieClipPreview(tripId, assetId, override, output = {}) {
  // JSON round-trip strips `undefined` deep inside grade objects (Firestore
  // rejects undefined field values).
  const clean = JSON.parse(JSON.stringify({ ...override, resultTo: 'moviePreview' }));
  await assetRef(tripId, assetId).set(
    {
      clipGradeRender: {
        status: 'queued',
        output: {
          source: output.source === 'master' ? 'master' : 'proxy',
          heightTarget: Number(output.heightTarget) > 0 ? Math.round(Number(output.heightTarget)) : 1080,
          quality: ['standard', 'high', 'max'].includes(output.quality) ? output.quality : 'high',
        },
        override: clean,
        requestedAt: Date.now(),
      },
    },
    { merge: true }
  );
}

// Save a non-destructive crop/reframe ("zoom") onto a photo OR clip asset.
// `crop` is a normalized {x,y,w,h} rect (+ optional rotate/flipH/flipV) in
// source-frame space, or null for the full frame. Photos bake it into display
// derivatives + every export; clips bake it into the rendered MP4. The master /
// source is left full-frame so it can be re-cropped any time.
export async function updateAssetCrop(tripId, assetId, crop) {
  const clean = crop && Number(crop.w) > 0 && Number(crop.h) > 0 ? crop : null;
  await assetRef(tripId, assetId).set({ crop: clean }, { merge: true });
}

// Save an ANIMATED reframe ("keyframed zoom") onto a clip asset. `reframeKeys`
// is [{ id, t, rect:{x,y,w,h} }] — a push-in / pull-out the clip render bakes by
// animating the crop window per frame (then high-quality scaling to a constant
// output size). Empty/one-full-key clears it back to the static crop.
export async function updateClipReframeKeys(tripId, assetId, reframeKeys) {
  const clean = Array.isArray(reframeKeys)
    ? reframeKeys.filter((k) => k && k.rect && Number(k.rect.w) > 0 && Number(k.rect.h) > 0)
    : [];
  await assetRef(tripId, assetId).set({ reframeKeys: clean.length ? clean : null }, { merge: true });
}

// Viewer-selectable soundtracks for a single clip (up to 4) plus which one
// plays first. Overlaid live at playback (see VideoPlayer), so no re-render.
export async function updateAssetSongs(tripId, assetId, { viewerSongs, viewerSongDefault }) {
  await assetRef(tripId, assetId).set(
    {
      viewerSongs: Array.isArray(viewerSongs) ? viewerSongs.slice(0, 4) : [],
      viewerSongDefault: viewerSongDefault || null,
    },
    { merge: true }
  );
}

// Bake the current grade and emit the requested delivery formats on the backend.
// `formats` is a list from EXPORT_FORMATS (magazine/fulljpeg/tiff16/png8/webp);
// the always-on display thumb + full-res display JPEG are regenerated regardless.
export async function requestPhotoGrade(tripId, assetId, grade, formats, stages, opts = {}) {
  const patch = {
    gradeRender: {
      status: 'queued',
      requestedAt: Date.now(),
      ...(Array.isArray(formats) && formats.length ? { formats } : {}),
      // Optional pro-workflow stage exports: [{ id:'negative'|'neutral'|'final',
      // formats:[...] }]. Baked as separate 16-bit artifacts keyed by stage.
      ...(Array.isArray(stages) && stages.length ? { stages } : {}),
      // DRAFT = a fast, low-res "dial the look" bake (no upscale, no fulljpeg).
      // Always set the flag explicitly so a full Export clears a prior draft.
      draft: !!opts.draft,
    },
  };
  if (grade !== undefined) patch.grade = grade;
  await assetRef(tripId, assetId).set(patch, { merge: true });
}

// ---- Grade presets / styles -----------------------------------------------
export const useGradePresets = () => useCollection('gradePresets', { orderBy: 'createdAt' });

export async function addGradePreset({ name, grade, kind = 'any' }) {
  const ref = db.collection('gradePresets').doc();
  await ref.set({ name: name || 'Preset', grade, kind, createdAt: now() });
  return ref.id;
}

export async function deleteGradePreset(presetId) {
  await db.collection('gradePresets').doc(presetId).delete();
}

// ---- Clip grading sessions ------------------------------------------------
// A clip-centric loop: seed proofs from a clip → give clip-wide / per-proof
// natural-language feedback (color/zoom/more frames) → live preview + re-bake →
// AI summaries → authorize a fan-out bake of every frame in the clip. The
// session lives on the clip asset's `gradeSession`; proofs are normal frame-grab
// photos tagged `clip-proof` with `proofOf == clipId` and a frozen `autoGradeBase`.

const sortByFrame = (a, b) =>
  (a.stillGrab?.frameIndex ?? a.lineage?.sourceTimeSec ?? 0) -
  (b.stillGrab?.frameIndex ?? b.lineage?.sourceTimeSec ?? 0);

// Live list of a clip's proof photos (everything pointing back at this clip).
export function useClipProofs(tripId, clipId) {
  const [proofs, setProofs] = useState(null);
  useEffect(() => {
    if (!ready() || !tripId || !clipId) { setProofs([]); return undefined; }
    const unsub = db
      .collection('trips').doc(tripId).collection('assets')
      .where('proofOf', '==', clipId)
      .onSnapshot(
        (snap) => setProofs(snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort(sortByFrame)),
        () => setProofs([])
      );
    return unsub;
  }, [tripId, clipId]);
  return proofs;
}

const emptyLayer = () => ({ ops: [], crop: null });

// Start (or reset) a grading session on a clip. Idempotent — only seeds the
// shape if absent so re-entering the page never clobbers in-progress feedback.
export async function startClipSession(tripId, clipId, { fps = 0, frameSet = [] } = {}) {
  if (!tripId || !clipId) return;
  const snap = await assetRef(tripId, clipId).get();
  const existing = snap.exists ? snap.data().gradeSession : null;
  if (existing && existing.status) return; // already started
  await assetRef(tripId, clipId).set(
    {
      gradeSession: {
        status: 'drafting',
        clipLayer: emptyLayer(),
        feedback: [],
        clipSummary: null,
        frameSet: Array.isArray(frameSet) ? frameSet : [],
        fps: Number(fps) || 0,
        fullBakeJobId: null,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      },
    },
    { merge: true }
  );
}

// Persist the clip-wide delta layer (the global feedback look).
export async function updateClipLayer(tripId, clipId, clipLayer) {
  const layer = {
    ops: Array.isArray(clipLayer?.ops) ? clipLayer.ops.filter((o) => o && o.key) : [],
    crop: clipLayer?.crop && Number(clipLayer.crop.w) > 0 ? clipLayer.crop : null,
  };
  await assetRef(tripId, clipId).set(
    { gradeSession: { clipLayer: layer, status: 'drafting', updatedAt: Date.now() } },
    { merge: true }
  );
}

// Store the session-level AI/deterministic summary.
export async function updateClipSummary(tripId, clipId, clipSummary) {
  await assetRef(tripId, clipId).set(
    { gradeSession: { clipSummary: clipSummary || null, updatedAt: Date.now() } },
    { merge: true }
  );
}

// Append one durable feedback log entry (the chat record this session is built
// around — fixes the old in-memory-only assistant).
export async function appendFeedback(tripId, clipId, entry) {
  const e = {
    role: entry.role === 'assistant' ? 'assistant' : 'user',
    text: String(entry.text || '').slice(0, 1200),
    scope: entry.scope === 'clip' || /^proof:/.test(entry.scope || '') ? entry.scope : 'clip',
    ops: Array.isArray(entry.ops) ? entry.ops : [],
    at: Date.now(),
  };
  await assetRef(tripId, clipId).set(
    { gradeSession: { feedback: firebase.firestore.FieldValue.arrayUnion(e), updatedAt: Date.now() } },
    { merge: true }
  );
}

// Persist a per-proof override (deltas + optional crop) for the next re-bake.
export async function setProofOverride(tripId, proofId, override) {
  const ov = {
    ops: Array.isArray(override?.ops) ? override.ops.filter((o) => o && o.key) : [],
    crop: override?.crop && Number(override.crop.w) > 0 ? override.crop : null,
  };
  await assetRef(tripId, proofId).set({ override: ov }, { merge: true });
}

const stillRenderJob = (name) => ({
  kind: 'still',
  status: 'queued',
  title: name || 'proof',
  etaSec: estimateRenderSec('still', true),
  costEstUsd: Math.round(renderCostUsd(estimateRenderSec('still', true)) * 1e4) / 1e4,
  requestedAt: Date.now(),
});

// Re-bake one proof with the current clip layer + its override. When the frozen
// auto base already exists we COMPOSE the absolute grade on the client (cheap,
// no re-perception); otherwise we ask the worker to auto-grade the base first.
export async function requeueProof(tripId, proof, clipLayer) {
  if (!proof) return;
  const sg = proof.stillGrab || {};
  const layer = clipLayer || emptyLayer();
  const override = proof.override || null;
  const base = proof.autoGradeBase || null;
  const patch = {
    status: 'rendering',
    renderJob: stillRenderJob(proof.name),
  };
  if (base) {
    const grade = composeProofGrade(base, layer, override);
    const crop = resolveProofCrop(proof.crop || null, layer, override);
    patch.grade = grade;
    patch.crop = crop;
    patch.stillGrab = {
      ...sg,
      status: 'queued',
      autoGrade: false,
      applyGrade: true,
      grade,
      crop,
      clipLayer: layer,
      override,
      autoGradeBase: base,
      requeuedAt: Date.now(),
    };
  } else {
    patch.stillGrab = {
      ...sg,
      status: 'queued',
      autoGrade: true,
      applyGrade: true,
      clipLayer: layer,
      override,
      requeuedAt: Date.now(),
    };
  }
  await assetRef(tripId, proof.id).set(patch, { merge: true });
}

// Ask the worker to build (or rebuild) the edit tree for one still on demand.
// Edit trees are no longer baked on every render (they were the dominant cost);
// the worker peels the stored negative into per-stage thumbnails when the user
// actually opens the tree. The doc's `editTreeStatus` drives the UI spinner.
export async function requestEditTree(tripId, assetId) {
  if (!tripId || !assetId) return;
  await assetRef(tripId, assetId).set(
    { editTreeStatus: 'queued', editTreeRequestedAt: Date.now() },
    { merge: true }
  );
}

// Re-bake every proof in a session (after clip-wide feedback changes the look).
export async function requeueAllProofs(tripId, proofs, clipLayer) {
  for (const p of proofs || []) {
    // eslint-disable-next-line no-await-in-loop
    await requeueProof(tripId, p, clipLayer);
  }
}

// Add new proof frames to a session by frame index (exact picks or AI suggestions).
export async function addProofFrames(tripId, clip, frames, clipLayer) {
  const fps = Number(clip?.srcFps || clip?.fpsAvg || clip?.metadata?.video?.fpsAvg || 0) || 24;
  const ids = [];
  for (const fi of frames || []) {
    const frameIndex = Math.round(Number(fi));
    if (!Number.isFinite(frameIndex) || frameIndex < 0) continue;
    // eslint-disable-next-line no-await-in-loop
    const id = await createStillGrabAsset(tripId, {
      source: clip,
      timeSec: frameIndex / fps,
      frameIndex,
      applyGrade: true,
      autoGrade: true,
      clipLayer: clipLayer || emptyLayer(),
      redSource: !!clip?.isRawVideo,
      proofOf: clip.id,
      tags: ['clip-proof'],
    });
    ids.push(id);
  }
  return ids;
}

// All existing frame-grab photos that came from this clip — the set re-baked
// when the session is authorized (in-place, preserving frameIndex + crop).
export async function getClipFrameAssets(tripId, clipId) {
  const snap = await db
    .collection('trips').doc(tripId).collection('assets')
    .where('lineage.sourceAssetId', '==', clipId)
    .get();
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((a) => a.kind === 'photo' && a.stillGrab);
}

// Authorize the full-clip bake: enumerate every frame grabbed from this clip,
// flip each to a queued auto-grade carrying the session's clip layer, and write
// a fan-out job doc the orchestrator/runner drives (atomic claim prevents the
// always-on worker and runners from double-baking). Returns the job id.
// Promote the per-proof AI auto-QC corrections into a single clip-wide layer so
// the full bake inherits them for free (no vision call per frame). For each
// control the model touched, take the MEDIAN delta across ALL reviewed proofs
// (missing = 0) — a correction only carries to the clip if most proofs needed
// it (e.g. a systematic shadow lift on a backlit clip), and one-off per-frame
// tweaks wash out. Returns merged ops (user layer + AI median), summed by key.
export function promoteAiClipOps(assets, userOps = []) {
  const proofs = (assets || []).filter((a) => Array.isArray(a?.aiCritique?.ops) && a.aiCritique.ops.length);
  const merged = new Map();
  for (const o of userOps || []) {
    if (o && o.key && Number.isFinite(Number(o.delta))) merged.set(o.key, Number(o.delta));
  }
  if (proofs.length) {
    const keys = new Set();
    for (const p of proofs) for (const o of p.aiCritique.ops) if (o && o.key) keys.add(o.key);
    for (const key of keys) {
      const vals = proofs.map((p) => {
        const op = p.aiCritique.ops.find((o) => o && o.key === key);
        return op ? Number(op.delta) || 0 : 0; // proofs without this correction count as 0
      });
      vals.sort((a, b) => a - b);
      const mid = Math.floor(vals.length / 2);
      const median = vals.length % 2 ? vals[mid] : (vals[mid - 1] + vals[mid]) / 2;
      if (Math.abs(median) > 1e-6) merged.set(key, (merged.get(key) || 0) + median);
    }
  }
  return [...merged.entries()]
    .filter(([, d]) => Math.abs(d) > 1e-6)
    .map(([key, delta]) => ({ key, delta: Math.round(delta * 1000) / 1000 }));
}

export async function authorizeClipBake(tripId, clip, clipLayer) {
  const clipId = clip.id;
  const baseLayer = clipLayer || emptyLayer();
  const assets = await getClipFrameAssets(tripId, clipId);
  if (!assets.length) throw new Error('no frames found for this clip yet — grab some first');
  // Fold the AI auto-QC corrections learned on the proofs into the clip layer so
  // every frame of the full bake inherits the same fix.
  const aiOps = promoteAiClipOps(assets, baseLayer.ops || []);
  const layer = { ops: aiOps, crop: baseLayer.crop || null };

  const jobRef = db.collection('jobs').doc();
  const assetIds = assets.map((a) => a.id);
  await jobRef.set({
    kind: 'clip-stills',
    status: 'queued',
    tripId,
    clipId,
    title: `Bake ${assets.length} frames — ${clip.name || 'clip'}`,
    assetIds,
    clipLayer: layer,
    total: assets.length,
    done: 0,
    createdAt: Date.now(),
  });

  // Flip each frame to queued auto-grade with the clip layer. The worker/runner
  // recomputes the per-frame base then composes the layer (preserves crop/index).
  let batch = db.batch();
  let ops = 0;
  for (const a of assets) {
    const sg = a.stillGrab || {};
    batch.set(
      assetRef(tripId, a.id),
      {
        status: 'rendering',
        renderJob: stillRenderJob(a.name),
        proofOf: clipId,
        stillGrab: {
          ...sg,
          status: 'queued',
          autoGrade: true,
          applyGrade: true,
          clipLayer: layer,
          override: null,
          claimJobId: jobRef.id,
          requeuedAt: Date.now(),
        },
      },
      { merge: true }
    );
    ops += 1;
    if (ops >= 400) { await batch.commit(); batch = db.batch(); ops = 0; } // eslint-disable-line no-await-in-loop
  }
  if (ops) await batch.commit();

  await assetRef(tripId, clipId).set(
    {
      gradeSession: {
        status: 'authorized',
        fullBakeJobId: jobRef.id,
        frameSet: assetIds,
        authorizedAt: Date.now(),
        updatedAt: Date.now(),
      },
    },
    { merge: true }
  );
  return jobRef.id;
}
